import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Send, History, Plus, Loader2, Trash2, ClipboardList, Users, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { EmptyState } from "@/components/EmptyState";
import { PercentBar } from "@/components/PercentBar";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UomSelect } from "@/components/UomSelect";
import { LabourManagementTab } from "@/components/project/LabourManagementSearch";
import { fmtDate, fmtDateTime, fmtNum, todayISO, partyLabel } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";
import { confirmAction } from "@/lib/dialogs";

export default function Construction() {
  const { projectId, canWrite } = useProjectCtx();
  const { can } = usePermissions();
  const projectRoleCanWrite = canWrite("CONSTRUCTION");
  // updating progress is PATCH /projects/{id}/construction/... -> the Progress row's edit
  const canEdit = projectRoleCanWrite && can("PROJECT_PROGRESS", "edit");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/construction`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);
  const noScope = leafItems.filter((i) => !(Number(i.quantity) > 0));   // % complete cannot be measured without a scope quantity

  return (
    <div className="space-y-4" data-testid="construction-page">
      <PageHeader
        title="Construction — Site Progress"
        description={canEdit ? "Actual start/end dates fill in automatically once JMRs against a linked Work Order are certified." : "Read-only for your project role."}
        action={canEdit && (
          <Button onClick={() => setUpdating(true)} data-testid="construction-update-button">
            <Send className="h-4 w-4" /> Update
          </Button>
        )}
      />
      <Tabs defaultValue="construction" data-testid="construction-tabs">
        <TabsList>
          <TabsTrigger value="construction" data-testid="construction-tab-construction">Construction</TabsTrigger>
          <TabsTrigger value="labour" data-testid="construction-tab-labour"><Users className="mr-1.5 h-3.5 w-3.5" /> Labour Management</TabsTrigger>
          <TabsTrigger value="requisitions" data-testid="construction-tab-requisitions"><ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Service Requisitions</TabsTrigger>
          <TabsTrigger value="log" data-testid="construction-tab-log"><History className="mr-1.5 h-3.5 w-3.5" /> Update Log</TabsTrigger>
        </TabsList>

        <TabsContent value="construction" className="space-y-4">
          {noScope.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200" data-testid="construction-no-scope-banner">
              <b>{noScope.length} of {leafItems.length} activities have no scope quantity</b>, so their progress cannot be measured and shows 0%.
              Set the quantity of each on the <Link to={`/projects/${projectId}/wbs`} className="font-medium underline">WBS &amp; Weightages</Link> page.
            </div>
          )}
          <div className="rounded-xl border border-border bg-card">
            {!loading && leafItems.length === 0 ? (
              <EmptyState title="No construction activities" testId="construction-empty-state" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Code</TableHead><TableHead>Activity</TableHead><TableHead>UoM</TableHead><TableHead>Scope</TableHead><TableHead>Progress</TableHead><TableHead>Actual start</TableHead><TableHead>Actual end</TableHead><TableHead>Manpower</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {leafItems.map((i) => {
                    const c = i.construction || {};
                    const scope = Number(i.quantity || 0);
                    const actualPct = scope > 0 ? Math.min(Math.max(Number(c.cum_actual_qty || 0) / scope, 0), 1) : 0;
                    const plannedPct = scope > 0 ? Math.min(Math.max(Number(c.cum_planned_qty || 0) / scope, 0), 1) : 0;
                    return (
                      <TableRow key={i.id} data-testid={`construction-row-${i.id}`}>
                        <TableCell className="font-mono text-xs">{i.code}</TableCell>
                        <TableCell className="text-foreground">{i.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{i.uom || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {scope > 0 ? `${fmtNum(c.cum_actual_qty)} / ${fmtNum(scope)}` : (
                            <span className="whitespace-nowrap rounded-md border border-amber-400/70 bg-amber-50 px-1.5 py-0.5 font-sans text-[11px] text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" data-testid={`construction-no-scope-${i.id}`}>Set the scope</span>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[130px]">
                          <div className="flex items-center gap-2">
                            <PercentBar actual={actualPct} planned={plannedPct} testId={`construction-percent-bar-${i.id}`} />
                            <span className="font-mono text-xs">{(actualPct * 100).toFixed(0)}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(c.actual_start_date)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(c.actual_end_date)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{fmtNum(c.present_manpower)}</TableCell>
                        <TableCell className="text-right">
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-construction-btn-${i.id}`} title="Full edit"><Pencil className="h-3.5 w-3.5" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="labour">
          <LabourManagementTab projectId={projectId} />
        </TabsContent>

        <TabsContent value="requisitions">
          <ConstructionRequisitions projectId={projectId} canEdit={projectRoleCanWrite} leafItems={leafItems} />
        </TabsContent>

        <TabsContent value="log">
          <UpdateLog projectId={projectId} />
        </TabsContent>
      </Tabs>
      {editing && <ConstructionDialog projectId={projectId} item={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {updating && <QuickUpdateDialog projectId={projectId} items={leafItems} onClose={() => setUpdating(false)} onSaved={load} />}
    </div>
  );
}

function UpdateLog({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/progress-entries`, { params: { category: "CONSTRUCTION", limit: 100 } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const METRIC_LABELS = { SCHEDULE_DATE: "Date", QUANTITY_CUMULATIVE: "Corrected total", QUANTITY_DELTA: "Today's output" };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="construction-update-log">
      {!loading && rows.length === 0 ? (
        <EmptyState title="No updates yet" description="Daily output, date milestones — and automatic JMR-driven updates — will show up here." testId="construction-log-empty-state" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Activity</TableHead><TableHead>What changed</TableHead><TableHead>Before → After</TableHead><TableHead>Updated by</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const fieldLabel = r.sub_stage
                ? r.sub_stage.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : METRIC_LABELS[r.metric] || r.metric.replaceAll("_", " ");
              const after = r.value_date ? fmtDate(r.value_date) : r.status_value ?? (r.quantity !== null ? fmtNum(r.quantity) : "—");
              const before = r.previous_value || "—";
              return (
                <TableRow key={r.id} data-testid={`construction-log-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</TableCell>
                  <TableCell className="text-xs">
                    {r.item_code ? <span className="font-mono text-foreground">{r.item_code}</span> : "—"}
                    {r.item_name && <div className="text-[10px] text-muted-foreground">{r.item_name}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fieldLabel}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <span className="text-muted-foreground">{before}</span> <span className="text-muted-foreground">→</span> <span className="font-semibold text-foreground">{after}</span>
                  </TableCell>
                  <TableCell className="text-xs">{r.entered_by_name || <span className="italic text-muted-foreground">Unknown</span>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function QuickUpdateDialog({ projectId, items, onClose, onSaved }) {
  const [itemId, setItemId] = useState("");
  const [mode, setMode] = useState("today"); // today | running | date
  const [amount, setAmount] = useState("");
  const [dateField, setDateField] = useState("actual_start_date");
  const [dateValue, setDateValue] = useState("");
  const [saving, setSaving] = useState(false);
  const item = items.find((i) => i.id === itemId);
  const c = item?.construction || {};

  const chooseItem = (v) => {
    setItemId(v);
    setMode("today");
    setAmount("");
    setDateValue(todayISO());
  };

  const submit = async () => {
    if (!item) return;
    setSaving(true);
    try {
      const payload = { entry_date: todayISO() };
      if (mode === "today") payload.progress_qty = Number(amount);
      else if (mode === "running") payload.cumulative_qty = Number(amount);
      else payload[dateField] = dateValue || null;
      await api.patch(`/projects/${projectId}/construction/${item.id}`, payload);
      toast.success("Construction progress updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const amountIsValid = amount !== "" && Number.isFinite(Number(amount)) && Number(amount) >= 0;
  const canSave = item && ((mode !== "date" && amountIsValid) || (mode === "date" && dateValue));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="construction-quick-update-dialog">
        <DialogHeader><DialogTitle>Update today's progress</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Activity</Label>
            <Select value={itemId} onValueChange={chooseItem}>
              <SelectTrigger data-testid="construction-update-item-select" className="mt-1.5"><SelectValue placeholder="Choose an activity" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {item && (
            <>
              <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-1 gap-2">
                <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                  <RadioGroupItem value="today" data-testid="construction-mode-today" /> Log today's output
                </label>
                <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                  <RadioGroupItem value="running" data-testid="construction-mode-running" /> Correct running total
                </label>
                <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                  <RadioGroupItem value="date" data-testid="construction-mode-date" /> Set a date (usually automatic)
                </label>
              </RadioGroup>
              {mode !== "date" ? (
                <div>
                  <Input type="number" min="0" step="0.001" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={mode === "today" ? "Quantity completed today" : "New corrected cumulative total"} data-testid="construction-update-amount-input" />
                  <p className="mt-1 text-[11px] text-muted-foreground">Current cumulative: <span className="font-mono">{fmtNum(c.cum_actual_qty)}</span> {item.uom}</p>
                </div>
              ) : (
                <div>
                  <Select value={dateField} onValueChange={setDateField}>
                    <SelectTrigger className="mb-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="actual_start_date">Actual start (usually automatic from JMR)</SelectItem>
                      <SelectItem value="actual_end_date">Actual end (usually automatic from JMR)</SelectItem>
                      <SelectItem value="scheduled_start_date">Scheduled start</SelectItem>
                      <SelectItem value="scheduled_end_date">Scheduled end</SelectItem>
                      <SelectItem value="forecast_end_date">Forecast end</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} data-testid="construction-update-date-input" />
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!canSave || saving} data-testid="construction-update-submit-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConstructionRequisitions({ projectId, canEdit, leafItems }) {
  const { companies } = useCompany() || {};
  const { project } = useProjectCtx();
  const { can } = usePermissions();
  const canRaise = canEdit && can("SERVICE_REQUISITIONS", "create");
  const canDelete = canEdit && can("SERVICE_REQUISITIONS", "delete");
  const canApprove = can("SERVICE_REQUISITIONS", "approve");
  const [srs, setSrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingSr, setEditingSr] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const [uomById, setUomById] = useState({});

  useEffect(() => {
    api.get("/masters/uom").then(({ data }) => setUomById(Object.fromEntries((data || []).map((u) => [u.id, u.code])))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/service-requisitions");
      setSrs((data || []).filter((r) => r.project_id === projectId).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";
  const activityName = (wbsItemId) => {
    const it = leafItems.find((i) => i.id === wbsItemId);
    return it ? `${it.code} — ${it.name}` : null;
  };

  const act = async (sr, action, message) => {
    setBusyId(sr.id);
    try {
      await api.post(`/procurement/service-requisitions/${sr.id}/${action}`);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const deleteSr = async (sr) => {
    if (!await confirmAction(`Delete draft service requisition ${sr.number}? This cannot be undone.`)) return;
    setBusyId(sr.id);
    try {
      await api.delete(`/procurement/service-requisitions/${sr.id}`);
      toast.success(`${sr.number} deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="construction-requisitions-tab">
      <div className="flex justify-end">
        {canRaise && <RaiseSrDialog projectId={projectId} companies={companies} defaultCompanyId={project?.company_id} leafItems={leafItems} onCreated={load} />}
        {canRaise && editingSr && <RaiseSrDialog key={editingSr.id} projectId={projectId} companies={companies} defaultCompanyId={project?.company_id} leafItems={leafItems} requisition={editingSr} onCreated={load} onClose={() => setEditingSr(null)} />}
      </div>
      {!loading && srs.length === 0 ? (
        <EmptyState title="No service requisitions for this project yet" description="Raise one for work that needs a subcontractor." testId="construction-requisitions-empty-state" />
      ) : (
        <div className="space-y-3">
          {srs.map((sr) => (
            <div key={sr.id} className="rounded-xl border border-border bg-card p-4" data-testid={`construction-sr-card-${sr.number}`}>
              <div className="flex items-center justify-between">
                <button type="button" className="flex items-center gap-2 text-left" onClick={() => toggleExpand(sr.id)} data-testid={`construction-sr-toggle-${sr.number}`}>
                  {expanded[sr.id] ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div>
                    <p className="font-mono text-xs text-muted-foreground">{sr.number}</p>
                    <p className="text-sm text-foreground">{companyName(sr.company_id)} · {sr.requisition_date}</p>
                    <p className="text-[10px] text-muted-foreground/70">Added {fmtDateTime(sr.created_at)} · {sr.items.length} item{sr.items.length === 1 ? "" : "s"}</p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <StatusPill value={sr.status} />
                  {canRaise && sr.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => setEditingSr(sr)} data-testid={`edit-construction-sr-${sr.number}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  )}
                  {canDelete && sr.status === "DRAFT" && (
                    <Button size="sm" variant="ghost" disabled={busyId === sr.id} onClick={() => deleteSr(sr)} title="Delete draft" data-testid={`delete-construction-sr-${sr.number}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                  {canRaise && sr.status === "DRAFT" && (
                    <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "submit", `${sr.number} submitted for approval`)} data-testid={`submit-construction-sr-${sr.number}`}>
                      <Send className="h-3.5 w-3.5" /> Submit
                    </Button>
                  )}
                  {canApprove && sr.status === "SUBMITTED" && (
                    <>
                      <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "approve",
                        sr.performed_by === "COMPANY_LABOUR"
                          ? `${sr.number} approved — scope added to Construction DPR. Add workers for it in Labour Management.`
                          : sr.preferred_vendor_id ? `${sr.number} approved — a draft Work Order was created automatically` : `${sr.number} approved — you can now make a Work Order from it`
                      )} title="Approve" data-testid={`approve-construction-sr-${sr.number}`}>
                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                      </Button>
                      <Button size="sm" variant="outline" disabled={busyId === sr.id} onClick={() => act(sr, "reject", `${sr.number} rejected`)} title="Reject" data-testid={`reject-construction-sr-${sr.number}`}>
                        <X className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    </>
                  )}
                  {sr.status === "APPROVED" && !sr.work_order_id && sr.performed_by !== "COMPANY_LABOUR" && (
                    <span className="text-[11px] text-sky-600 dark:text-sky-400">Ready for a Work Order</span>
                  )}
                  {sr.status === "CONVERTED" && sr.performed_by === "COMPANY_LABOUR" && (
                    <span className="text-[11px] text-violet-600 dark:text-violet-400">Applied to Construction DPR</span>
                  )}
                  {sr.status === "CONVERTED" && sr.work_order_id && (
                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400">Work Order created</span>
                  )}
                </div>
              </div>
              {expanded[sr.id] && (
                <>
                  {sr.scope_of_work && <p className="mt-2 text-xs text-muted-foreground">{sr.scope_of_work}</p>}
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                        <tr><th className="pb-1 pr-3 text-left">Description</th><th className="pb-1 pr-3 text-right">Qty</th><th className="pb-1 text-left">Activity</th></tr>
                      </thead>
                      <tbody>
                        {sr.items.map((line) => (
                          <tr key={line.id} className="border-t border-border/50">
                            <td className="py-1 pr-3 text-foreground">{line.description}</td>
                            <td className="py-1 pr-3 text-right font-mono">{Number(line.quantity)}{line.uom_id && uomById[line.uom_id] ? ` ${uomById[line.uom_id]}` : ""}</td>
                            <td className="py-1">
                              {activityName(line.wbs_item_id) && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-foreground">{activityName(line.wbs_item_id)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_SR_ITEM = { description: "", quantity: "", uom_id: "", wbs_item_id: "" };

function RaiseSrDialog({ projectId, companies, defaultCompanyId, leafItems, requisition, onCreated, onClose }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    company_id: requisition?.company_id || defaultCompanyId || "",
    requisition_date: requisition?.requisition_date || todayISO(),
    scope_of_work: requisition?.scope_of_work || "",
    performed_by: requisition?.performed_by || "CONTRACTOR",
    preferred_vendor_id: requisition?.preferred_vendor_id || "",
  });
  const [items, setItems] = useState([{ ...EMPTY_SR_ITEM }]);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!requisition) return;
    setOpen(true);
    setItems((requisition.items || []).map((item) => ({
      description: item.description,
      quantity: String(item.quantity),
      uom_id: item.uom_id || "",
      wbs_item_id: item.wbs_item_id || "",
    })));
  }, [requisition]);

  useEffect(() => {
    if (!open && !requisition) return;
    api.get("/procurement/vendors").then(({ data }) => setVendors(data || [])).catch(() => {});
  }, [open, requisition]);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Choosing an activity fills the line: description, quantity (what is still left of its scope) and unit.
  const pickActivity = async (idx, wbsItemId) => {
    const activity = leafItems.find((i) => i.id === wbsItemId);
    const patchItem = (p) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...p } : it)));
    patchItem({ wbs_item_id: wbsItemId, description: activity ? `${activity.code} — ${activity.name}` : "", _hint: "" });
    if (!activity) return;
    try {
      const { data: bal } = await api.get("/procurement/sr-activity-balance", { params: { project_id: projectId, wbs_item_id: wbsItemId } });
      const key = (u) => String(u || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      let uomId = "";
      try {
        const { data: uoms } = await api.get("/masters/uom");
        uomId = (uoms || []).find((u) => key(u.code) === key(bal.uom))?.id || "";
      } catch { /* unit stays blank */ }
      const remaining = Number(bal.remaining);
      const unit = bal.uom || "";
      if (remaining > 0) {
        patchItem({ quantity: String(remaining), ...(uomId ? { uom_id: uomId } : {}),
          _hint: `Scope ${Number(bal.scope)} ${unit} · already requested ${Number(bal.requisitioned)} · remaining ${remaining}` });
      } else {
        patchItem({ ...(uomId ? { uom_id: uomId } : {}),
          _hint: Number(bal.scope) > 0 ? `Whole scope (${Number(bal.scope)} ${unit}) is already requested. Enter a quantity only if you need more.` : "This activity has no scope quantity yet. Set it in Plan setup, or type a quantity." });
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, project_id: projectId, preferred_vendor_id: form.performed_by === "CONTRACTOR" ? (form.preferred_vendor_id || null) : null,
        items: items.map(({ _hint, ...it }) => ({ ...it, wbs_item_id: it.wbs_item_id || null })),
      };
      if (requisition) {
        await api.patch(`/procurement/service-requisitions/${requisition.id}`, payload);
        toast.success("Service requisition updated");
      } else {
        await api.post("/procurement/service-requisitions", payload);
        toast.success("Service requisition raised");
      }
      setItems([{ ...EMPTY_SR_ITEM }]);
      setOpen(false);
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) onClose?.(); }}>
      {!requisition && <DialogTrigger asChild><Button data-testid="raise-construction-sr-button"><Plus className="h-4 w-4" /> Raise Service Requisition</Button></DialogTrigger>}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{requisition ? `Edit ${requisition.number}` : "Raise Service Requisition — this project"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="raise-construction-sr-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="construction-sr-company-select" />
            <div><Label>Date</Label><Input required type="date" value={form.requisition_date} onChange={set("requisition_date")} className="mt-1.5" /></div>
          </div>
          <div><Label>Scope of work</Label><Textarea value={form.scope_of_work} onChange={set("scope_of_work")} className="mt-1.5" /></div>
          <div>
            <Label>Done by</Label>
            <RadioGroup value={form.performed_by} onValueChange={(v) => setForm({ ...form, performed_by: v, preferred_vendor_id: "" })} className="mt-1.5 grid grid-cols-1 gap-2">
              <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="CONTRACTOR" className="mt-0.5" data-testid="sr-done-by-contractor" />
                <span><span className="font-medium">Subcontractor</span><br /><span className="text-xs text-muted-foreground">Needs a vendor — picking a preferred vendor below auto-creates a draft Work Order once approved</span></span>
              </label>
              <label className="flex items-start gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="COMPANY_LABOUR" className="mt-0.5" data-testid="sr-done-by-company" />
                <span><span className="font-medium">Company Labour</span><br /><span className="text-xs text-muted-foreground">No vendor — approving adds this quantity straight into Construction DPR's planned scope for each linked activity</span></span>
              </label>
            </RadioGroup>
          </div>
          {form.performed_by === "CONTRACTOR" && (
            <div>
              <Label>Preferred vendor <span className="font-normal text-muted-foreground">(optional — picking one auto-creates a draft Work Order once this is approved)</span></Label>
              <Select value={form.preferred_vendor_id || "none"} onValueChange={(v) => setForm({ ...form, preferred_vendor_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="sr-preferred-vendor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Decide later</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Items</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">Link each line to the activity it's for — its actual start/end dates fill in automatically once JMRs against it are certified.</p>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx}>
                <div className="flex items-center gap-2" data-testid={`construction-sr-item-row-${idx}`}>
                  <Select required value={it.wbs_item_id || ""} onValueChange={(v) => pickActivity(idx, v)}>
                    <SelectTrigger className="h-9 flex-1" data-testid={`construction-sr-item-activity-select-${idx}`}><SelectValue placeholder="Select activity" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {leafItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-20" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                  <UomSelect byId value={it.uom_id} onChange={(v) => updateItem(idx, "uom_id", v)} className="w-28" />
                  {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
                {it._hint && <div className="mt-1 text-[11px] text-muted-foreground">{it._hint}</div>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_SR_ITEM }])}><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} {requisition ? "Save changes" : "Create"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConstructionDialog({ projectId, item, onClose, onSaved }) {
  const c = item.construction || {};
  const [mode, setMode] = useState("today");
  const [amount, setAmount] = useState("");
  const [form, setForm] = useState({
    entry_date: todayISO(), note: "", scheduled_start_date: c.scheduled_start_date || "", scheduled_end_date: c.scheduled_end_date || "",
    actual_start_date: c.actual_start_date || "", forecast_end_date: c.forecast_end_date || "", actual_end_date: c.actual_end_date || "",
    cum_planned_qty: c.cum_planned_qty ?? "", present_manpower: c.present_manpower ?? "", avg_output_per_day: c.avg_output_per_day ?? "",
    status_remarks: c.status_remarks || "",
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const amountIsValid = amount === "" || (Number.isFinite(Number(amount)) && Number(amount) >= 0);

  const submit = async () => {
    if (!amountIsValid) return;
    try {
      const payload = {
        entry_date: form.entry_date, note: form.note || null,
        scheduled_start_date: form.scheduled_start_date || null, scheduled_end_date: form.scheduled_end_date || null,
        actual_start_date: form.actual_start_date || null, forecast_end_date: form.forecast_end_date || null, actual_end_date: form.actual_end_date || null,
        cum_planned_qty: form.cum_planned_qty === "" ? null : Number(form.cum_planned_qty),
        present_manpower: form.present_manpower === "" ? null : Number(form.present_manpower),
        avg_output_per_day: form.avg_output_per_day === "" ? null : Number(form.avg_output_per_day),
        status_remarks: form.status_remarks || null,
      };
      if (amount !== "") {
        if (mode === "today") payload.progress_qty = Number(amount);
        else payload.cumulative_qty = Number(amount);
      }
      await api.patch(`/projects/${projectId}/construction/${item.id}`, payload);
      toast.success("Construction progress updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="construction-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-gold-500/30 bg-gold-500/5 p-3">
            <Label className="text-xs uppercase tracking-wider text-gold-600 dark:text-gold-400">Daily quantity update — pick one</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="mt-2 grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="today" data-testid="daily-output-mode-today" /> Log today's output
              </label>
              <label className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="running" data-testid="daily-output-mode-running" /> Correct running total
              </label>
            </RadioGroup>
            <Input
              type="number" min="0" step="0.001" className="mt-2" data-testid="construction-amount-input"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder={mode === "today" ? "Quantity completed today" : "New corrected cumulative total"}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Current cumulative: <span className="font-mono">{fmtNum(c.cum_actual_qty)}</span> {item.uom}
            </p>
          </div>

          <div><Label>Entry date</Label><Input type="date" data-testid="construction-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Scheduled start</Label><Input type="date" data-testid="construction-sched-start-input" value={form.scheduled_start_date} onChange={set("scheduled_start_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled end</Label><Input type="date" data-testid="construction-sched-end-input" value={form.scheduled_end_date} onChange={set("scheduled_end_date")} className="mt-1.5" /></div>
            <div><Label>Actual start <span className="font-normal text-muted-foreground">(usually automatic)</span></Label><Input type="date" data-testid="construction-actual-start-input" value={form.actual_start_date} onChange={set("actual_start_date")} className="mt-1.5" /></div>
            <div><Label>Forecast end</Label><Input type="date" data-testid="construction-forecast-end-input" value={form.forecast_end_date} onChange={set("forecast_end_date")} className="mt-1.5" /></div>
            <div><Label>Actual end <span className="font-normal text-muted-foreground">(usually automatic)</span></Label><Input type="date" data-testid="construction-actual-end-input" value={form.actual_end_date} onChange={set("actual_end_date")} className="mt-1.5" /></div>
            <div><Label>Cum. planned qty</Label><Input type="number" min="0" data-testid="construction-cum-planned-input" value={form.cum_planned_qty} onChange={set("cum_planned_qty")} className="mt-1.5" /></div>
            <div><Label>Present manpower</Label><Input type="number" min="0" data-testid="construction-manpower-input" value={form.present_manpower} onChange={set("present_manpower")} className="mt-1.5" /></div>
            <div><Label>Avg output/day</Label><Input type="number" min="0" data-testid="construction-avg-output-input" value={form.avg_output_per_day} onChange={set("avg_output_per_day")} className="mt-1.5" /></div>
          </div>
          <div><Label>Status remarks</Label><Textarea data-testid="construction-status-remarks-input" value={form.status_remarks} onChange={set("status_remarks")} className="mt-1.5" /></div>
          <div><Label>Note for this entry</Label><Input data-testid="construction-note-input" value={form.note} onChange={set("note")} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!amountIsValid} data-testid="construction-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
